import type GlobalDataRepositoryInterface from '../interface-adapters/GlobalDataRepositoryInterface';
import axios, { HttpStatusCode } from 'axios';
import GetGlobalDataResult from '../core-models/GetGlobalDataResult';
import StringHelper from '@/core/helpers/StringHelper';
import LivestreamProductFactory from '@/livestream/features/livestream-product/factories/LivestreamProductFactory';
import LivestreamProductEntity from '@/livestream/features/livestream-product/entities/LivestreamProductEntity';
import { ORDER_PAGE_MODE, type OrderPageMode } from '@/presentation/constants/OrderPageModeConstants';

export default class GlobalDataRepository implements GlobalDataRepositoryInterface {
    async get(mode: OrderPageMode, productId: number, ref: string): Promise<GetGlobalDataResult> {
        const result = new GetGlobalDataResult();
        const isAffiliate = mode === ORDER_PAGE_MODE.affiliate;
        // Lỗi mạng / 5xx / body không phải JSON → throw để react-query retry, không coi là "không có sản phẩm".
        const { status, data: responseBody } = await axios.get<GetGlobalDataResult>(
            import.meta.env.VITE_API_URL +
                (isAffiliate
                    ? import.meta.env.VITE_GET_AFFILIATE_GLOBAL_DATA_API_PATH
                    : import.meta.env.VITE_GET_LIVESTREAM_GLOBAL_DATA_API_PATH),
            {
                params: isAffiliate ? { productId, ...(ref ? { ref } : {}) } : undefined,
                withCredentials: true,
                validateStatus: () => true,
            },
        );
        if (status >= HttpStatusCode.InternalServerError || typeof responseBody?.success !== 'boolean') {
            throw new Error(`Get global data failed (HTTP ${status})`);
        }
        const { success, message, data, customResponseCode } = responseBody;
        if (success === true) {
            result.success = true;
        }
        if (StringHelper.isHasValue(message)) {
            result.message = message;
        }
        if (customResponseCode) {
            result.customResponseCode = customResponseCode;
        }
        const rawProducts = Array.isArray(data?.products) ? data.products : [];
        const mapper = LivestreamProductFactory.makeMapper();
        const mappedProducts = rawProducts
            .map(p => (isAffiliate ? mapper.mapFromAffiliateApiToEntity(p) : mapper.mapFromApiToEntity(p)))
            .filter((p): p is LivestreamProductEntity => p !== null);

        result.data = {
            customerSourceId: Number(data?.customerSourceId) > 0 ? Math.floor(Number(data.customerSourceId)) : 0,
            qrDiscountPercent: (() => {
                const pct = Math.floor(Number(data?.qrDiscountPercent));
                if (!Number.isFinite(pct) || pct < 0) {
                    return 0;
                }
                return pct > 100 ? 100 : pct;
            })(),
            customerInfo: data?.customerInfo || null,
            products: mappedProducts,
        };

        return result;
    }
}
